import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@arco-design/web-react';

import { CheckExpandable } from '@/components/CheckExpandable';

import type { CommandArgsNormalPaths } from '../command-args-normal';
import {
  CommandArgsNormalBlock,
  ensureCommandArgsNormalDefaults,
  hasInitCommandAtFormPaths,
  readInitCommandFromFormPaths,
  resolveCommandArgsNormalPaths,
} from '../command-args-normal';
import styles from './index.module.less';
import type { MaasCommandArgsNormalBlockProps } from './types';

const INIT_COMMAND_PREFIX = 'initCommand';

/** MaaS 全局初始化命令：CheckExpandable + CommandArgsNormalBlock */
export function MaasCommandArgsNormalBlock({ form, options, ...control }: MaasCommandArgsNormalBlockProps) {
  const { t } = useTranslation();
  const prefix = options?.pathPrefix ?? INIT_COMMAND_PREFIX;
  const paths = resolveCommandArgsNormalPaths(prefix) as CommandArgsNormalPaths;
  const hideExpandCheckbox = options?.hideExpandCheckbox === true;
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (hideExpandCheckbox) return;
    const apiInit = readInitCommandFromFormPaths(form, paths);
    setExpanded(!!apiInit);
  }, [form, hideExpandCheckbox, paths]);

  useEffect(() => {
    if (!hideExpandCheckbox) return;
    ensureCommandArgsNormalDefaults(form, paths);
    if (!hasInitCommandAtFormPaths(form, paths) && !form.getFieldValue(paths.shellType)) {
      form.setFieldValue(paths.shellType, 'bash');
      form.setFieldValue(paths.commands, 'bash -c');
    }
  }, [form, hideExpandCheckbox, paths]);

  const handleExpandChange = useCallback(
    (checked: boolean) => {
      setExpanded(checked);
      if (!checked) {
        form.setFieldValue(paths.shellType, undefined);
        form.setFieldValue(paths.commands, undefined);
        form.setFieldValue(paths.args, undefined);
      } else if (!hasInitCommandAtFormPaths(form, paths)) {
        form.setFieldValue(paths.shellType, 'bash');
        form.setFieldValue(paths.commands, 'bash -c');
      }
    },
    [form, paths],
  );

  const commandOptions = { noStyle: false, scriptHint: options?.scriptHint };
  const commandBlock = <CommandArgsNormalBlock {...control} form={form} paths={paths} options={commandOptions} />;

  const renderCard = (content: ReactNode) => (
    <Card className={styles.configCard} bordered={false}>
      <CheckExpandable
        label={t('data-center:maas.init.command.and.args')}
        checked={expanded}
        onChange={handleExpandChange}
        contentStyle={{ paddingTop: 8 }}
      >
        {content}
      </CheckExpandable>
    </Card>
  );

  if (hideExpandCheckbox) {
    if (control.blockMode === 'preview') {
      const apiInit = readInitCommandFromFormPaths(form, paths);
      if (!apiInit) return null;
    }
    return commandBlock;
  }

  if (control.blockMode === 'preview') {
    const apiInit = readInitCommandFromFormPaths(form, paths);
    if (!apiInit) return renderCard(null);
    return renderCard(<CommandArgsNormalBlock {...control} form={form} paths={paths} blockMode="preview" options={commandOptions} />);
  }

  if (!expanded) {
    return renderCard(null);
  }

  return renderCard(commandBlock);
}

export default MaasCommandArgsNormalBlock;
